import {
	isJSONRPCRequest,
	isJSONRPCResponse,
	JSONRPCClient,
	JSONRPCServer,
	JSONRPCServerAndClient,
} from "json-rpc-2.0";

type SocketEventListener = (...args: any[]) => void;

type SocketEvent = keyof WebSocketEventMap | (string & {});

export type TokenProvider = () => Promise<string | null>;

export class WebSocketService {
	#baseUrl: string;
	#socket: WebSocket | null = null;

	#tokenProvider: TokenProvider;

	#retryCount = 0;
	#retryTimer: ReturnType<typeof setTimeout> | null = null;
	#isManuallyDisconnected = false;

	#listeners = new Map<SocketEvent, SocketEventListener[]>();
	#rpc: JSONRPCServerAndClient;

	#reservedEvents: (keyof WebSocketEventMap)[] = ["open", "close"];

	constructor(url: string, tokenProvider: TokenProvider) {
		this.#baseUrl = url;
		this.#tokenProvider = tokenProvider;

		this.#rpc = new JSONRPCServerAndClient(
			new JSONRPCServer(),
			new JSONRPCClient((payload) => {
				if (this.#socket == null) {
					return;
				}

				if (this.#socket.readyState === WebSocket.OPEN) {
					this.#socket.send(JSON.stringify(payload));
				}
			})
		);
	}

	get connected() {
		return this.#socket != null && this.#socket.readyState === WebSocket.OPEN;
	}

	async connect() {
		this.#isManuallyDisconnected = false;

		if (this.#socket?.readyState === WebSocket.CONNECTING) {
			return;
		}

		this.#clearRetryTimer();

		const token = await this.#tokenProvider();
		if (token == null) {
			return;
		}

		if (this.#socket) {
			this.#socket.close();
			this.#socket = null;
		}

		const url = new URL(this.#baseUrl);

		// In case there are pending request, following a disconnect.
		this.#rpc.rejectAllPendingRequests("Disconnected");

		return new Promise<void>((resolve, reject) => {
			const socket = new WebSocket(url);
			this.#socket = socket;

			this.#socket.addEventListener("message", async (msg) => {
				try {
					const payload = JSON.parse(msg.data);
					if (isJSONRPCResponse(payload)) {
						this.#rpc.client.receive(payload);
					} else if (isJSONRPCRequest(payload)) {
						this.#rpc.server.receive(payload);
					}
				} catch (e) {
					console.error("Failed to parse websocket json data", e);
				}
			});

			this.#socket.addEventListener("open", async () => {
				try {
					await this.request("Auth.Login", { token });
					this.#retryCount = 0;

					this.#emit("open");
					resolve();
				} catch (e) {
					this.#socket?.close();
					reject(e);
				}
			});

			this.#socket.addEventListener("error", async (evt) => {
				console.error("Websocket error: ", evt);
			});

			this.#socket.addEventListener("close", async () => {
				// Socket changed, don't fire the retry logic, to prevent race condition
				if (this.#socket !== socket) {
					return;
				}

				this.#socket = null;
				this.#emit("close");

				this.#scheduleRetry();
			});
		});
	}

	disconnect() {
		this.#isManuallyDisconnected = true;
		this.#clearRetryTimer();

		if (this.#socket) {
			this.#socket.close();
			this.#socket = null;
		}
	}

	on(name: SocketEvent, listener: SocketEventListener) {
		const listeners = this.#listeners.get(name) || [];
		listeners.push(listener);
		this.#listeners.set(name, listeners);

		if (this.#reservedEvents.includes(name as any) || this.#rpc.hasMethod(name)) {
			return;
		}

		this.#rpc.addMethod(name, (data) => {
			const listeners = this.#listeners.get(name) || [];
			for (const listener of listeners) {
				listener(data);
			}
		});
	}

	off(name: SocketEvent, listener: SocketEventListener) {
		let listeners = this.#listeners.get(name) || [];

		listeners = listeners.filter((l) => l !== listener);
		this.#listeners.set(name, listeners);

		if (!this.#reservedEvents.includes(name as any) && listeners.length === 0) {
			this.#rpc.removeMethod(name);
		}
	}

	async request(method: string, data: any) {
		if (this.#socket == null) {
			return;
		}

		await this.#rpc.request(method, data);
	}

	async notify(method: string, data: any) {
		this.#rpc.notify(method, data);
	}

	#emit(name: SocketEvent, ...args: any[]) {
		const listeners = this.#listeners.get(name) || [];
		for (const listener of listeners) {
			listener(...args);
		}
	}

	#clearRetryTimer() {
		if (this.#retryTimer) {
			clearTimeout(this.#retryTimer);
			this.#retryTimer = null;
		}
	}

	#scheduleRetry() {
		if (this.#isManuallyDisconnected || this.#retryCount >= 10) {
			return;
		}

		// Exp backoff with a limit of 10 secondes
		const delay = Math.min(1_000 * Math.pow(2, this.#retryCount), 10_000);

		this.#retryTimer = setTimeout(() => {
			this.#retryCount++;
			this.connect().catch(() => {});
		}, delay);
	}
}
